import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ScanLine, Camera, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

export const ScanMobile = () => {
  const { token } = useParams();
  const [state, setState] = useState("validating"); // validating|ready|uploading|done|error|expired
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const [docType, setDocType] = useState("carte_grise");
  const camRef = useRef(null);

  useEffect(() => {
    api.get(`/public/scan/${token}`)
      .then(({ data }) => { setDocType(data.doc_type || "carte_grise"); setState("ready"); })
      .catch((e) => setState(e?.response?.status === 410 ? "expired" : "error"));
  }, [token]);

  const isInvoice = docType === "invoice";
  const docLabel = isInvoice ? "la facture" : "la carte grise";

  const upload = async (f) => {
    if (!f) return;
    setState("uploading");
    setErrMsg("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/public/scan/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data.result);
      setState("done");
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || "Lecture impossible");
      setState("ready");
    }
  };

  return (
    <div className="min-h-screen bg-[#05060E] text-white flex flex-col items-center px-6 py-10">
      <div className="flex items-center gap-2 mb-8">
        <ScanLine className="w-6 h-6 text-aurora-cyan" />
        <span className="font-display font-bold uppercase tracking-tight text-lg">IMPERIUM</span>
      </div>

      <div className="w-full max-w-sm glass rounded-lg p-6 text-center">
        {state === "validating" && (
          <div className="py-10 flex flex-col items-center gap-3 text-zinc-400">
            <Loader2 className="w-7 h-7 animate-spin text-aurora-cyan" />
            <span className="font-mono text-xs">Vérification du lien…</span>
          </div>
        )}

        {(state === "ready" || state === "uploading") && (
          <>
            <h1 className="font-display text-2xl font-bold uppercase tracking-tight mb-2">Scanner {docLabel}</h1>
            <p className="font-mono text-[11px] text-zinc-400 mb-6 leading-relaxed">
              Prenez une photo nette et bien cadrée de {isInvoice ? "votre facture ou bon de commande" : "votre certificat d'immatriculation"}. Elle sera envoyée directement à votre ordinateur.
            </p>
            {errMsg && (
              <div className="mb-4 p-2.5 rounded-sm bg-signal-error/10 border border-signal-error/40 flex items-center gap-2 text-left">
                <AlertTriangle className="w-4 h-4 text-signal-error shrink-0" />
                <span className="text-[11px] text-signal-error">{errMsg}</span>
              </div>
            )}
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="scan-mobile-input" onChange={(e) => upload(e.target.files?.[0])} />
            <button
              data-testid="scan-mobile-capture"
              disabled={state === "uploading"}
              onClick={() => camRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 py-4 rounded-md bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider disabled:opacity-60"
            >
              {state === "uploading" ? <><Loader2 className="w-5 h-5 animate-spin" /> Analyse en cours…</> : <><Camera className="w-5 h-5" /> Prendre la photo</>}
            </button>
          </>
        )}

        {state === "done" && (
          <div className="py-6" data-testid="scan-mobile-done">
            <CheckCircle2 className="w-14 h-14 text-signal-success mx-auto mb-4" />
            <h1 className="font-display text-xl font-bold uppercase tracking-tight mb-1">Document envoyé !</h1>
            {result && (
              <p className="font-mono text-sm text-aurora-cyan mb-3">{[result.brand || result.company_name, result.model || result.city, result.year].filter(Boolean).join(" ")}</p>
            )}
            <p className="font-mono text-[11px] text-zinc-400">Retournez sur votre <b className="text-white">ordinateur</b> — le formulaire s'est rempli automatiquement.</p>
          </div>
        )}

        {state === "expired" && (
          <div className="py-10 text-center">
            <AlertTriangle className="w-12 h-12 text-signal-error mx-auto mb-4" />
            <p className="font-mono text-sm text-zinc-300">Ce lien a expiré.</p>
            <p className="font-mono text-[11px] text-zinc-500 mt-1">Relancez le scan depuis votre ordinateur.</p>
          </div>
        )}

        {state === "error" && (
          <div className="py-10 text-center">
            <AlertTriangle className="w-12 h-12 text-signal-error mx-auto mb-4" />
            <p className="font-mono text-sm text-zinc-300">Lien invalide.</p>
          </div>
        )}
      </div>

      <p className="font-mono text-[9px] text-zinc-600 mt-8 uppercase tracking-widest">Imperium · Automotive Files</p>
    </div>
  );
};

export default ScanMobile;
